import React, { useState } from 'react';
import {
  Table as TTable,
  Column,
  ColumnDef,
  RowData,
  flexRender,
  SortingState,
} from '@tanstack/react-table';
import styled from '@mui/material/styles/styled';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  useGroupedData,
  useArraySelection,
  GroupBy,
  IsParent,
  useVirtualTable,
  GroupedRow,
} from './hooks';

declare module '@tanstack/table-core' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    [x: string]: any;
  }
}

export type { ColumnDef, GroupedRow };

const GDSHeadCell = styled(TableCell)(({ theme }) => ({
  background: theme.palette.grey.A100,
  borderBottom: `2px solid ${theme.palette.primary.main}`,
  fontSize: theme.typography.body1.fontSize,
  lineHeight: `calc(${theme.typography.body1.fontSize} * 3.3)`,
  outline: 'transparent !important',
  padding: `0 ${theme.spacing(1.25)} !important`,
  whiteSpace: 'nowrap',
}));

const GDSCell = styled(TableCell)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey[100]}`,
  borderTop: `1px solid ${theme.palette.grey.A100}`,
  padding: `${theme.spacing(1)} ${theme.spacing(1.25)} !important`,
}));

const GDSRow = styled(TableRow)(({ theme }) => ({
  '&:hover, &.selected': {
    background: theme.palette.primary.light,
  },
}));

const TableCheckbox = styled(Checkbox)(({ theme }) => ({
  color: theme.palette.primary.main,
  padding: '0 !important',
}));

const getCellStyle = (column: Column<any>) => ({
  maxWidth: column.getSize(),
  minWidth: column.getSize(),
  width: column.getSize(),
});

/* eslint-disable sort-keys */
const selectColumn: ColumnDef<any> = {
  id: 'select',
  enableSorting: false,
  size: 20,
  header: ({ table }) => (
    <TableCheckbox
      {...{
        checked: table.getIsAllRowsSelected(),
        indeterminate: table.getIsSomeRowsSelected(),
        onChange: table.getToggleAllRowsSelectedHandler(),
      }}
    />
  ),
  cell: ({ row }) => (
    <TableCheckbox
      {...{
        checked: row.getIsSelected(),
        disabled: !row.getCanSelect(),
        indeterminate: row.getIsSomeSelected(),
        onChange: row.getToggleSelectedHandler(),
      }}
    />
  ),
};
/* eslint-enable sort-keys */

export interface GDSDataGridProps<RData> {
  [x: string]: any;
  NoRowsOverlay?: (table?: TTable<RData>) => JSX.Element;
  checkboxSelection?: boolean;
  columns: ColumnDef<RData>[];
  disableVirtualization?: boolean;
  getRowId?: (row: RData, index: number) => string;
  groupBy?: GroupBy<RData>;
  isParent?: IsParent<RData>;
  onSelectionModelChange?: (selection: string[]) => void;
  rows: RData[];
  selectionModel?: string[];
  sortBy?: SortingState;
  stickyHeader?: boolean;
}

export default function GDSDataGrid<RData>({
  checkboxSelection,
  columns,
  disableVirtualization,
  getRowId = (_: RData, index: number) => index.toString(),
  NoRowsOverlay,
  onSelectionModelChange,
  rows: _data,
  selectionModel,
  sortBy,
  stickyHeader,
  groupBy,
  isParent,
  ...props
}: Readonly<GDSDataGridProps<RData>>) {
  const [sorting, setSorting] = useState<SortingState>(sortBy || []);
  const data = useGroupedData<RData>({ data: _data, groupBy, isParent });

  const { onRowSelectionChange, rowSelection } = useArraySelection({
    data: _data,
    getRowId,
    onSelectionModelChange,
    selectionModel,
  });

  const { paddingBottom, paddingTop, rows, table, tableContainerRef } =
    useVirtualTable<RData>({
      columns: checkboxSelection
        ? [selectColumn as ColumnDef<RData>, ...columns]
        : columns,
      data,
      getRowId,
      onRowSelectionChange,
      onSortingChange: setSorting,
      state: {
        rowSelection,
        sorting,
      },
    });

  const showOverlay = _data.length === 0 && NoRowsOverlay;

  return (
    <TableContainer
      ref={tableContainerRef}
      sx={
        showOverlay
          ? {
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
            }
          : {
              overflow: 'auto',
              position: 'relative',
            }
      }
    >
      <Table
        component="div"
        stickyHeader={stickyHeader}
        style={{ width: '100%' }}
      >
        <TableHead component="div">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow component="div" id={headerGroup.id} key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <GDSHeadCell
                  component="div"
                  id={header.id}
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                  style={{
                    ...getCellStyle(header.column),
                    ...(header.column.getCanSort() && { cursor: 'pointer' }),
                  }}
                >
                  <Stack alignItems="center" flexDirection="row">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {header.column.getIsSorted() === 'asc' && (
                      <IconButton sx={{}}>
                        <ExpandMoreIcon color="primary" />
                      </IconButton>
                    )}
                    {header.column.getIsSorted() === 'desc' && (
                      <IconButton sx={{}}>
                        <ExpandLessIcon color="primary" />
                      </IconButton>
                    )}
                  </Stack>
                </GDSHeadCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        {!showOverlay && (
          <TableBody component="div">
            {paddingTop > 0 && (
              <GDSRow>
                <GDSCell
                  component="div"
                  style={{ height: `${paddingTop}px` }}
                />
              </GDSRow>
            )}
            {rows.map((row) => (
              <GDSRow
                className={row.getIsSelected() ? 'selected' : ''}
                id={row.id}
                key={row.id}
              >
                {row.getVisibleCells().map((cell) => (
                  <GDSCell
                    component="div"
                    id={cell.id}
                    key={cell.id}
                    style={getCellStyle(cell.column)}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </GDSCell>
                ))}
              </GDSRow>
            ))}
            {paddingBottom > 0 && (
              <GDSRow>
                <GDSCell
                  component="div"
                  style={{ height: `${paddingBottom}px` }}
                />
              </GDSRow>
            )}
          </TableBody>
        )}
      </Table>
      {showOverlay && <Box sx={{ flexGrow: 1 }}>{NoRowsOverlay(table)}</Box>}
    </TableContainer>
  );
}
